/**
 * AudioManager - Handles audio preloading and caching for instant playback
 */

type LoadingState = 'idle' | 'preloading' | 'loading' | 'ready' | 'error';

interface AudioEntry {
  blob: Blob;
  size: number;
  // objectUrl is no longer stored — created fresh on demand
}

interface LoadingListener {
  onStateChange: (state: LoadingState) => void;
}

class AudioManager {
  private audioCache = new Map<string, AudioEntry>();
  private loadingStates = new Map<string, LoadingState>();
  private listeners = new Map<string, Set<LoadingListener>>();
  private abortControllers = new Map<string, AbortController>();
  private directUrls = new Map<string, string>(); // Direct URLs for RSS streams
  
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.echonour.com';
  }

  /**
   * Subscribe to loading state changes for a video
   */
  subscribe(videoId: string, listener: LoadingListener): () => void {
    if (!this.listeners.has(videoId)) {
      this.listeners.set(videoId, new Set());
    }
    this.listeners.get(videoId)!.add(listener);
    
    // Immediately notify current state
    const currentState = this.loadingStates.get(videoId) || 'idle';
    listener.onStateChange(currentState);
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(videoId)?.delete(listener);
    };
  }

  private notifyListeners(videoId: string, state: LoadingState) {
    this.loadingStates.set(videoId, state);
    this.listeners.get(videoId)?.forEach(listener => {
      listener.onStateChange(state);
    });
  }

  /**
   * Get auth headers for API requests
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem("tempo_token");
    const guestSession = localStorage.getItem("tempo_guest_session");
    
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (guestSession) {
      headers['X-Guest-Session'] = guestSession;
    }
    return headers;
  }

  /**
   * Preload audio for instant start - fetches first chunk immediately,
   * then continues loading full file in background.
   * 
   * @param videoId - The video ID
   * @param directUrl - Optional direct URL for RSS streams (bypasses API)
   */
  async preloadAudio(videoId: string, directUrl?: string): Promise<void> {
    // Already cached or loading
    if (this.audioCache.has(videoId) || this.audioCache.has(`${videoId}_preload`)) {
      this.notifyListeners(videoId, 'ready');
      return;
    }
    
    const currentState = this.loadingStates.get(videoId);
    if (currentState === 'preloading' || currentState === 'loading') {
      return; // Already in progress
    }

    this.notifyListeners(videoId, 'preloading');
    
    // If directUrl provided (RSS stream), store it and mark ready immediately
    // The audio element will load from the URL directly when played
    if (directUrl) {
      this.directUrls.set(videoId, directUrl);
      this.notifyListeners(videoId, 'ready');
      return;
    }
    
    const controller = new AbortController();
    this.abortControllers.set(videoId, controller);

    try {
      // Fetch with preload=true for first chunk (fast start)
      const preloadUrl = `${this.baseUrl}/audio/${encodeURIComponent(videoId)}?preload=true`;
      const response = await fetch(preloadUrl, { 
        signal: controller.signal,
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      
      this.audioCache.set(`${videoId}_preload`, {
        blob,
        size: blob.size,
      });
      
      this.notifyListeners(videoId, 'ready');
      
      // Continue loading full file in background (don't await)
      this.loadFullAudio(videoId);
      
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('[AudioManager] Preload failed:', err);
        this.notifyListeners(videoId, 'error');
      }
    } finally {
      this.abortControllers.delete(videoId);
    }
  }

  /**
   * Load full audio file in background
   */
  private async loadFullAudio(videoId: string): Promise<void> {
    // Skip if already have full audio
    if (this.audioCache.has(videoId)) {
      return;
    }

    const controller = new AbortController();
    this.abortControllers.set(`${videoId}_full`, controller);

    try {
      const fullUrl = `${this.baseUrl}/audio/${encodeURIComponent(videoId)}`;
      const response = await fetch(fullUrl, { 
        signal: controller.signal,
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      
      this.audioCache.set(videoId, {
        blob,
        size: blob.size,
      });
      
      // Clean up preload entry - we have the full file now
      this.audioCache.delete(`${videoId}_preload`);
      
      console.log(`[AudioManager] Full audio cached: ${videoId} (${(blob.size / 1024).toFixed(1)}KB)`);
      this.notifyListeners(videoId, 'ready');
      
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('[AudioManager] Full load failed:', err);
      }
    } finally {
      this.abortControllers.delete(`${videoId}_full`);
    }
  }

  /**
   * Get audio URL - returns cached blob URL if available, otherwise direct URL
   */
  getAudioUrl(videoId: string, preloadOnly = false): string {
    // Check for direct URL (RSS streams) first
    const directUrl = this.directUrls.get(videoId);
    if (directUrl) {
      return directUrl;
    }
    
    // Prefer full audio. Only return a partial preload blob when explicitly requested;
    // using it as an <audio> src makes the browser believe the whole episode is ~2MB.
    // Always create a fresh objectUrl from the cached blob — stale objectUrls cause ERR_FILE_NOT_FOUND
    const fullEntry = this.audioCache.get(videoId);
    if (fullEntry) {
      return URL.createObjectURL(fullEntry.blob);
    }
    
    const preloadEntry = this.audioCache.get(`${videoId}_preload`);
    if (preloadOnly && preloadEntry) {
      return URL.createObjectURL(preloadEntry.blob);
    }
    
    // Not fully cached - return API URL with token for auth
    // HTML audio elements can't send headers, so we pass token in URL
    const token = localStorage.getItem("tempo_token");
    const baseUrl = preloadOnly 
      ? `${this.baseUrl}/audio/${encodeURIComponent(videoId)}?preload=true`
      : `${this.baseUrl}/audio/${encodeURIComponent(videoId)}`;
    
    const separator = baseUrl.includes('?') ? '&' : '?';
    if (token) {
      return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
    }

    const guestSession = localStorage.getItem("tempo_guest_session");
    if (guestSession) {
      return `${baseUrl}${separator}guest_session=${encodeURIComponent(guestSession)}`;
    }
    
    return baseUrl;
  }

  /**
   * Check if audio is cached (preload or full)
   */
  isCached(videoId: string): boolean {
    return this.audioCache.has(videoId) || this.audioCache.has(`${videoId}_preload`);
  }

  /**
   * Get current loading state
   */
  getLoadingState(videoId: string): LoadingState {
    return this.loadingStates.get(videoId) || 'idle';
  }

  /**
   * Cancel in-progress loading
   */
  cancelLoading(videoId: string): void {
    this.abortControllers.get(videoId)?.abort();
    this.abortControllers.get(`${videoId}_full`)?.abort();
    this.abortControllers.delete(videoId);
    this.abortControllers.delete(`${videoId}_full`);
  }

  /**
   * Clear cache for a specific video
   */
  clearVideo(videoId: string): void {
    this.cancelLoading(videoId);
    this.audioCache.delete(videoId);
    this.audioCache.delete(`${videoId}_preload`);
    this.loadingStates.delete(videoId);
    this.listeners.delete(videoId);
  }

  /**
   * Clear entire cache
   */
  clearCache(): void {
    // Cancel all loading
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers.clear();
    this.audioCache.clear();
    this.loadingStates.clear();
    this.listeners.clear();
    
    console.log('[AudioManager] Cache cleared');
  }

  /**
   * Get cache stats for debugging
   */
  getCacheStats(): { entries: number; totalSize: number } {
    let totalSize = 0;
    this.audioCache.forEach(entry => {
      totalSize += entry.size;
    });
    return {
      entries: this.audioCache.size,
      totalSize,
    };
  }
}

// Singleton instance
export const audioManager = new AudioManager();

// React hook for audio loading state
import { useState, useEffect } from 'react';

export function useAudioLoading(videoId: string | null): LoadingState {
  const [state, setState] = useState<LoadingState>('idle');
  
  useEffect(() => {
    if (!videoId) {
      setState('idle');
      return;
    }
    
    const unsubscribe = audioManager.subscribe(videoId, {
      onStateChange: setState,
    });
    
    return unsubscribe;
  }, [videoId]);
  
  return state;
}
